/**
 * Quick Nav action dispatcher — maps `NavTarget.openDialog` slugs to the
 * appropriate destination-page action.
 *
 * Two kinds of actions:
 *
 * 1. **Store actions** — the destination page has a global Zustand dialog
 *    store (`useScreenDialogStore`, `useAssetDialogStore`, etc.). The
 *    dispatcher calls `store.getState().openDialog(...)` directly after
 *    navigation; Zustand state persists across route transitions so the
 *    dialog renders once the destination component mounts.
 *
 * 2. **Page-local actions** — the trigger is a page-local hook (Transloadit
 *    `openUploadModal`, `handleTemplateClick`, or scio1 `openAddTeamModal`
 *    via `useScio1TeamModal`). These can't be called from outside their
 *    hosting component, so the dispatcher appends `?action=<slug>` to the
 *    route and the destination page's `useEffect` reads and clears it.
 *
 * Design principle: registry (`nav-targets.ts`) stays pure data — every
 * cross-feature import lives HERE, in one file. Adding a new action = one
 * new case here + one registry entry.
 */

import type { ApolloClient } from '@apollo/client';
import { useAccountMembersDialogStore } from '@/features/account-members/stores/account-members-dialog-store';
import { useAssetDialogStore } from '@/features/assets/stores/asset-dialog-store';
import { prefetchUploadOptions } from '@/features/assets/hooks/useTransloaditUpload';
import { useEngageWizardStore } from '@/features/engage/stores/engage-wizard-store';
import { useMdmFleetsDialogStore } from '@/features/mdm-fleets/stores/mdm-fleets-dialog-store';
import { usePlaylistDialogStore } from '@/features/playlists/stores/playlist-dialog-store';
import { useProvisioningTemplatesDialogStore } from '@/features/provisioning-templates/stores/provisioning-templates-dialog-store';
import { useScheduleDialogStore } from '@/features/schedules/stores/schedule-dialog-store';
import { useScreenDialogStore } from '@/features/screens/stores/screen-dialog-store';
import { useAppSelectorStore } from '@/stores/app-selector-store';
import { useTeamStore } from '@/stores/team-store';
import type { TeamWithMembers } from '@scio3/shared-types';
import type { NavTarget } from './nav-targets';

/**
 * Scope an account-members dialog to the currently active team.
 *
 * The three per-team dialogs (Add User, Invite Member, Manage Members) only
 * read `team._id` + `team.name` — both present on `MeTeam` — so we can shim
 * a `TeamWithMembers` payload from `useTeamStore` without a full Apollo
 * team fetch. On Default Team we pass no context and let the dialogs render
 * their "Add to team: Default Team" fallback (matches the page's own click
 * handlers on the Default Team section — no team argument, same result).
 */
function activeTeamDialogContext(): { team?: TeamWithMembers } {
  const { isDefaultTeam } = useTeamStore.getState();
  if (isDefaultTeam) return {};
  const active = useTeamStore.getState().getActiveTeam();
  if (!active) return {};
  return { team: active as unknown as TeamWithMembers };
}

/**
 * Slugs whose action is triggered by a page-local hook — dispatched via a
 * `?action=<slug value>` URL param the destination page reads on mount.
 * The value here is what the page checks in `searchParams.get('action')`.
 *
 * Only `asset-upload` truly needs this: Uppy's `useTransloaditUpload` mints
 * a per-mount instance with per-caller callbacks, so it can't be triggered
 * from outside AssetsPage without a global-state refactor.
 */
const PAGE_LOCAL_ACTIONS: Record<string, string> = {
  'asset-upload': 'upload',
  // Update Team — TeamDialog in edit mode needs `description` +
  // `limitation.maxQuantity` (not on `MeTeam`), so the dispatcher can't
  // build the payload. AccountMembersPage reads `?action=updateTeam` and
  // hands its own Apollo team object to `useScio1TeamModal.openUpdateTeamModal`.
  'account-update-team': 'updateTeam',
};

/**
 * Resolve a Quick Nav target into a concrete navigation + optional store
 * action. Callers do `navigate(route)` then run `storeAction?.()`.
 *
 * Returns just `{ route }` for pure-nav entries (no `openDialog`) or for
 * page-local actions (whose route already carries the `?action=` hint).
 * Store-backed actions return `{ route, storeAction }`.
 *
 * `apolloClient` is optional — when provided, the dispatcher warms any
 * query the destination page will need so the UI has no loading flash.
 * Currently only used for `asset-upload` (prefetches upload options so
 * Uppy builds without a network round-trip → no overlay flash).
 */
export function resolveNavAction(
  target: NavTarget,
  apolloClient?: ApolloClient,
): {
  route: string;
  storeAction?: () => void;
} {
  const slug = target.openDialog;
  if (!slug) return { route: target.route };

  // Page-local trigger — pack the action into a URL param and let the
  // destination page's effect run the real handler. Warm any pre-fetchable
  // queries now so the destination handler has no network wait.
  const paramValue = PAGE_LOCAL_ACTIONS[slug];
  if (paramValue) {
    if (slug === 'asset-upload' && apolloClient) {
      prefetchUploadOptions(apolloClient);
    }
    const separator = target.route.includes('?') ? '&' : '?';
    return { route: `${target.route}${separator}action=${paramValue}` };
  }

  // Store-backed trigger — pair the navigate call with a Zustand set.
  // The store's state persists across the route transition, so the dialog
  // renders as soon as the destination component mounts.
  switch (slug) {
    case 'screen-pair':
      return {
        route: target.route,
        storeAction: () => useScreenDialogStore.getState().openDialog('pair'),
      };
    case 'screen-preconfigure':
      return {
        route: target.route,
        storeAction: () => useScreenDialogStore.getState().openDialog('preconfigure'),
      };
    case 'screen-execute':
      return {
        route: target.route,
        storeAction: () => useScreenDialogStore.getState().openDialog('execute'),
      };
    case 'screen-export-devices-list':
      return {
        route: target.route,
        storeAction: () => useScreenDialogStore.getState().openDialog('exportDevicesList'),
      };

    case 'app-selector':
      // AppSelector is a global (not asset-page-scoped) store — from a
      // search click we don't have a current-folder context, so seed the
      // root path; the selector's own picker still lets the user re-target.
      return {
        route: target.route,
        storeAction: () => useAppSelectorStore.getState().openAppSelector({ currentPath: '/' }),
      };
    case 'asset-feeds':
      return {
        route: target.route,
        storeAction: () => useAssetDialogStore.getState().openDialog('feeds', { currentPath: '/' }),
      };

    case 'playlist-create':
      return {
        route: target.route,
        storeAction: () => usePlaylistDialogStore.getState().openDialog('createPlaylist'),
      };

    case 'schedule-create':
      return {
        route: target.route,
        storeAction: () => useScheduleDialogStore.getState().openDialog('createSchedule'),
      };
    case 'schedule-global-default':
      return {
        route: target.route,
        storeAction: () => useScheduleDialogStore.getState().openDialog('globalDefault'),
      };

    case 'account-add-user':
      return {
        route: target.route,
        storeAction: () =>
          useAccountMembersDialogStore.getState().openDialog('addUser', activeTeamDialogContext()),
      };
    case 'account-invite-member':
      return {
        route: target.route,
        storeAction: () =>
          useAccountMembersDialogStore
            .getState()
            .openDialog('inviteUser', activeTeamDialogContext()),
      };
    case 'account-manage-roles':
      return {
        route: target.route,
        storeAction: () => useAccountMembersDialogStore.getState().openDialog('manageRoles'),
      };
    case 'account-manage-members':
      return {
        route: target.route,
        storeAction: () =>
          useAccountMembersDialogStore
            .getState()
            .openDialog('manageMembers', activeTeamDialogContext()),
      };
    case 'account-add-team':
      // Directly open the native TeamDialog. `useScio1TeamModal` also
      // routes account-with-teamBilling to a scio1 iframe modal, but
      // that path needs hook context (Apollo lazy query + iframe
      // bridge state) so we skip it from the dispatcher — team-billing
      // accounts get the native dialog, which handles the basic team
      // create (no in-flow billing checkbox). Acceptable minor regression
      // for a rare cohort; users can still enable billing after creation.
      return {
        route: target.route,
        storeAction: () => useAccountMembersDialogStore.getState().openDialog('teamDialog'),
      };

    case 'mdm-fleet-create':
      return {
        route: target.route,
        storeAction: () => useMdmFleetsDialogStore.getState().openDialog('create'),
      };
    case 'mdm-template-create':
      return {
        route: target.route,
        storeAction: () => useProvisioningTemplatesDialogStore.getState().openDialog('addTemplate'),
      };
    case 'mdm-wifi-create':
      return {
        route: target.route,
        storeAction: () => useProvisioningTemplatesDialogStore.getState().openDialog('addWifi'),
      };
    case 'mdm-eap-create':
      return {
        route: target.route,
        storeAction: () =>
          useProvisioningTemplatesDialogStore.getState().openDialog('addEapProfile'),
      };

    case 'engage-add-app':
      return {
        route: target.route,
        storeAction: () => useEngageWizardStore.getState().openWizard({}),
      };

    default:
      // Unknown slug — fail open (just navigate). Registry additions
      // must add a case here or the store call is silently dropped.
      if (import.meta.env.DEV) {
        console.warn(
          `[nav-actions] Unknown openDialog slug: "${slug}" — navigating without action`,
        );
      }
      return { route: target.route };
  }
}
